import { FC, FormEvent, FocusEvent, KeyboardEvent, useEffect, useState, useRef } from "react";

import "./NumericInput.css";

export interface NumericInputProps {
  value: number;
  onChange: (value: number) => void;
  onEnter: () => void;
  autoFocus?: boolean;
}

const ALLOWED_KEYS = '0123456789'.split('');

export const NumericInput: FC<NumericInputProps> = ({ value, onChange, onEnter, autoFocus }) => {
  const [wasTouched, setTouched] = useState(false);
  const [hasError, setError] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!ref || !ref.current || !autoFocus) {
      return;
    }

    ref.current.focus();
  }, [ref.current, autoFocus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasError) {
      return;
    }

    setTimeout(() => setError(false), 300);
  }, [hasError]);

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!ALLOWED_KEYS.includes(e.key)) {
      e.preventDefault();
      setError(true);
    }

    if (e.key === "Enter") {
      onEnter();
      e.preventDefault();
    }
  };

  const handleChange = (e: FormEvent<HTMLInputElement>) => {
    if (!wasTouched) {
      setTouched(true);
    }

    if (!e.currentTarget.value) {
      return onChange(0);
    }

    onChange(parseInt(e.currentTarget.value, 10));
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    e.target.focus();
  }

  const handleFocus = () => {
    window.scrollTo(0, 0);
  }

  const getFontSize = () => {
    const { length } = value.toString();

    if (length <= 4) {
      return 6;
    }

    if (length === 5) {
      return 4.8;
    }

    if (length === 6) {
      return 4
    }

    if (length === 7) {
      return 3.4;
    }

    return 2.8;
  }

  const stringValue = value?.toString();

  return (
    <input
      ref={ref}
      value={wasTouched ? stringValue : undefined}
      placeholder={stringValue}
      className={`numeric-input ${hasError ? "numeric-input--error" : ""}`}
      inputMode="numeric"
      autoFocus={autoFocus}
      onKeyPress={handleKeyPress}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      style={{
        fontSize: `${getFontSize()}rem`
      }}
    />
  )
}